import { createFileRoute, useRouter } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { useForm } from "react-hook-form";
import { useEditUserMutation, USER_KEY, useUserQuery } from "@/modules/users/api/useUsers.ts";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { Card, CardTitle } from "@/components/ui/card.tsx";
import { Trans } from "@lingui/react/macro";
import { Separator } from "@/components/ui/separator.tsx";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { useDefaultFormValues } from "@/hooks/useDefaultFormValues.tsx";
import { UnassignUserModal } from "@/modules/users/UnassignUserModal.tsx";
import { logError } from "@/sentry.ts";
import { USERS_LOCATION_KEY } from "@/routes/settings/users/index.tsx";
import { getLocation } from "@/lib/table.ts";
import { useMeData } from "@/dataProviders/MeDataProvider.tsx";

type Inputs = { firstName: string; lastName: string; email: string; phoneNumber: string };

export const Route = createFileRoute("/settings/users/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const title = t`User detail`;
  const { activeApp } = useAppConfig();
  const { id } = Route.useParams();
  const me = useMeData();

  const { data, isPending, isError, error } = useUserQuery({ merchantId: activeApp, userId: id });
  const isEditingSelf = !!data?.email && data.email === me.email;

  const form = useForm<Inputs>({
    defaultValues: { phoneNumber: "", email: "", firstName: "", lastName: "" },
  });
  const isDirty = form.formState.isDirty;

  useDefaultFormValues(data, (values) => form.reset(values), id);

  const { mutateAsync } = useEditUserMutation({ merchantId: activeApp, userId: id });
  const queryClient = useQueryClient();
  const { navigate } = useRouter();

  const onSubmit = async ({ firstName, lastName, email, phoneNumber }: Inputs) => {
    try {
      await mutateAsync({ firstName, lastName, email, phoneNumber, role: "admin" });
      await queryClient.invalidateQueries({ queryKey: [USER_KEY] });
      toast.success(t`User was edited successfully.`);
      void navigate({ to: "/settings/users" });
    } catch (e) {
      logError(e);
      toast.error(t`Something went wrong. We were unable to update user. Please try again.`);
    }
  };

  return (
    <PageWrapper>
      <PageHeader
        breadcrumbs={[
          { href: "/settings/users/", title: t`Users administration` },
          { href: "/", title },
        ]}
      />
      <div className="flex flex-col grow p-5">
        {isError && (
          <FetchingError
            message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
            error={error}
          />
        )}
        {isPending && <Loader message={t`Loading users data...`} />}
        {data ? (
          <Card className={"shadow-none"}>
            <div className={"px-5"}>
              <CardTitle className={"text-xl font-semibold"}>
                <Trans>Basic information</Trans>
              </CardTitle>
            </div>
            <Separator />

            <Form {...form}>
              <form className={"px-5 w-full flex flex-col"} onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  rules={{ required: t`This field is required` }}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Trans>First name*</Trans>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={"Jan"} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  rules={{ required: t`This field is required` }}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className={"mt-3"}>
                      <FormLabel>
                        <Trans>Last name*</Trans>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={"Novák"} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  rules={{ required: t`This field is required` }}
                  render={({ field }) => (
                    <FormItem className={"mt-3"}>
                      <FormLabel>
                        <Trans>E-mail*</Trans>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={"mail@mail.com"} {...field} disabled={!isEditingSelf} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem className={"mt-3"}>
                      <FormLabel>
                        <Trans>Phone number</Trans>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={"+420 123 456 789"} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className={"flex gap-4 ml-auto mt-5 flex-wrap"}>
                  <UnassignUserModal
                    userId={id}
                    userName={`${data?.firstName} ${data?.lastName}`}
                    userEmail={data?.email}
                    buttonText={t`Unassign`}
                    shouldRedirect={true}
                  />
                  <Button
                    type={"button"}
                    variant={"secondary"}
                    onClick={() => navigate({ to: "/settings/users" + getLocation(USERS_LOCATION_KEY) })}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button type={"submit"} disabled={!isDirty}>
                    <Trans>Save</Trans>
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        ) : null}
      </div>
    </PageWrapper>
  );
}
